import { useState, useEffect, useCallback } from "react";

// ============================================================================
// brioCAST Admin Alert Console (B6, interim) — operator ticker overrides
// ============================================================================
// Admin-only page. Gated in App.jsx to PUB-ADMIN1 (UI gate only — NOT a
// security boundary; real auth is B5's server-side admin-JWT verification).
// API: /api/admin/ticker-overrides (B5 admin routes)
//
// AUTH: B5 admin endpoints require a separate admin JWT (type:'admin').
// The portal's biz_token (venue-owner login) must NOT be used here. Interim
// solution: operator pastes an admin JWT, stored in sessionStorage under
// 'briocast_admin_token' (NOT localStorage). Never hardcode/commit a token.
// ============================================================================

const ADMIN_API = "https://api.briolabs.io/api/admin/ticker-overrides";
const ADMIN_TOKEN_KEY = "briocast_admin_token";

const getAdminToken = () => sessionStorage.getItem(ADMIN_TOKEN_KEY) || "";

async function adminApi(path, options = {}) {
  const token = getAdminToken();
  const res = await fetch(`${ADMIN_API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  let json = null;
  try { json = await res.json(); } catch { /* no body */ }
  if (!res.ok) {
    const err = new Error((json && (json.error || json.message)) || "Request failed");
    err.status = res.status;
    throw err;
  }
  return json;
}

const t = {
  bg: "#0F1117", card: "#1A1D27", cardHover: "#22262F", border: "#2A2E3A",
  accent: "#6C5CE7", accentLight: "#A29BFE", accentBg: "rgba(108,92,231,0.12)",
  success: "#00B894", successBg: "rgba(0,184,148,0.12)",
  warning: "#FDCB6E", warningBg: "rgba(253,203,110,0.15)",
  danger: "#E17055", dangerBg: "rgba(225,112,85,0.12)",
  info: "#0984E3", infoBg: "rgba(9,132,227,0.12)",
  text: "#E8E8ED", muted: "#8B8FA3", dim: "#5A5E72",
  mono: "'JetBrains Mono', 'SF Mono', monospace",
};

const TIERS = {
  platform:  { label: "平台公告", color: t.info },
  cwa:       { label: "災害人工轉發 (CWA)", color: t.danger },
  emergency: { label: "緊急覆蓋", color: t.warning },
};
const SEVERITIES = {
  info:     { label: "一般", color: t.info, bg: t.infoBg },
  warning:  { label: "注意", color: t.warning, bg: t.warningBg },
  severe:   { label: "嚴重", color: t.danger, bg: t.dangerBg },
  critical: { label: "危急", color: t.danger, bg: t.dangerBg },
};
const DISPLAY_POLICIES = {
  top_ticker_pinned:       { label: "頂部跑馬燈置頂" },
  overlay_30s_repeat_120s: { label: "覆蓋 30 秒 / 每 120 秒重複" },
  ticker_only:             { label: "僅跑馬燈" },
};

// Tier defaults applied on tier change.
const TIER_DEFAULTS = {
  cwa:       { severity: "critical", display_policy: "overlay_30s_repeat_120s", clearVenue: true },
  emergency: { severity: "severe",   display_policy: "overlay_30s_repeat_120s" },
  platform:  { severity: "info",     display_policy: "top_ticker_pinned" },
};

const MAX_MSG = 200;
const TTL_PRESETS = [
  { label: "30 分", ms: 30 * 60000 },
  { label: "1 小時", ms: 60 * 60000 },
  { label: "2 小時", ms: 120 * 60000 },
  { label: "6 小時", ms: 360 * 60000 },
  { label: "24 小時", ms: 1440 * 60000 },
];

// The B5 server prepends this prefix and strips any 中央氣象署 claim for
// source=operator_manual. Preview must match server behaviour.
const DISASTER_PREFIX = "⚠️ brioCAST 災害警示：";
const CWA_CLAIM = "中央氣象署";
const CWA_PRESET_MESSAGE = "大雷雨快訊 請將汽機車移至安全處";

function fmt(d) {
  if (!d) return "—";
  const dt = new Date(d.includes?.("Z") ? d : d + "Z");
  const p = n => String(n).padStart(2, "0");
  return `${p(dt.getMonth() + 1)}/${p(dt.getDate())} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
}

// Build a value for <input type="datetime-local"> from a Date, in local time.
function toLocalInputValue(date) {
  const p = n => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
}

function Badge({ label, color, bg }) {
  return <span style={{ display: "inline-flex", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, color, background: bg || (color + "1f") }}>{label}</span>;
}

const labelStyle = { fontSize: 11, fontWeight: 600, color: t.muted, display: "block", marginBottom: 4, letterSpacing: 0.3 };
const fieldStyle = { width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid " + t.border, fontSize: 13, boxSizing: "border-box", background: t.card, color: t.text, fontFamily: "inherit" };

// ── Admin token panel ──
function TokenPanel({ token, onChange, onClear }) {
  const [draft, setDraft] = useState("");
  const present = !!token;
  return (
    <div style={{ background: t.card, borderRadius: 10, padding: 16, border: "1px solid " + (present ? t.success + "55" : t.warning + "55"), marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: t.text }}>🔐 Admin Token</span>
        {present
          ? <Badge label="已設定" color={t.success} bg={t.successBg} />
          : <Badge label="未設定" color={t.warning} bg={t.warningBg} />}
      </div>
      <div style={{ fontSize: 11, color: t.muted, lineHeight: 1.6, marginBottom: 10 }}>
        B5 admin 端點需要獨立的 admin JWT（type:'admin'），不可使用店家登入的 biz_token。請於下方貼上 admin token。
        Token 僅存於本次工作階段（sessionStorage），不會寫入 localStorage，也不會提交至程式庫。
      </div>
      {present ? (
        <button onClick={onClear} style={{ padding: "8px 16px", borderRadius: 8, border: "1.5px solid " + t.border, background: "none", color: t.danger, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>清除 admin token</button>
      ) : (
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="password"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="貼上 admin JWT…"
            autoComplete="off"
            style={{ ...fieldStyle, flex: 1, fontFamily: t.mono, fontSize: 12 }}
          />
          <button
            onClick={() => { if (draft.trim()) { onChange(draft.trim()); setDraft(""); } }}
            disabled={!draft.trim()}
            style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: draft.trim() ? t.accent : t.dim, color: "#fff", fontSize: 12, fontWeight: 700, cursor: draft.trim() ? "pointer" : "not-allowed", opacity: draft.trim() ? 1 : 0.6 }}
          >儲存</button>
        </div>
      )}
    </div>
  );
}

// ── Create form ──
function CreateForm({ disabled, onSubmit }) {
  const [tier, setTier] = useState("platform");
  const [severity, setSeverity] = useState(TIER_DEFAULTS.platform.severity);
  const [displayPolicy, setDisplayPolicy] = useState(TIER_DEFAULTS.platform.display_policy);
  const [scopeAll, setScopeAll] = useState(true); // true = 全部場域/全國 (venue_id null)
  const [venueId, setVenueId] = useState("");
  const [message, setMessage] = useState("");
  const [expiresAt, setExpiresAt] = useState(""); // datetime-local value (local time)
  const [busy, setBusy] = useState(false);

  const applyTier = (next) => {
    setTier(next);
    const d = TIER_DEFAULTS[next];
    if (d) {
      setSeverity(d.severity);
      setDisplayPolicy(d.display_policy);
      if (d.clearVenue) { setScopeAll(true); setVenueId(""); }
    }
  };

  const setTtl = (ms) => setExpiresAt(toLocalInputValue(new Date(Date.now() + ms)));
  const applyCwaPreset = () => {
    setTier("cwa");
    setSeverity("critical");
    setDisplayPolicy("overlay_30s_repeat_120s");
    setScopeAll(true);
    setVenueId("");
    setMessage(CWA_PRESET_MESSAGE);
    setExpiresAt(toLocalInputValue(new Date(Date.now() + 30 * 60000)));
  };

  const trimmedMsg = message.trim();
  const expiresDate = expiresAt ? new Date(expiresAt) : null;
  const expiresValid = expiresDate && !isNaN(expiresDate) && expiresDate.getTime() > Date.now();
  const isDisaster = tier === "cwa" || tier === "emergency";
  const claimsCwa = isDisaster && trimmedMsg.includes(CWA_CLAIM);
  const policyMismatch = isDisaster && displayPolicy !== "overlay_30s_repeat_120s";
  const emergencyNeedsVenue = tier === "emergency" && (scopeAll || !venueId.trim());

  const previewText = isDisaster ? DISASTER_PREFIX + trimmedMsg.replace(new RegExp(CWA_CLAIM, "g"), "").trim() : trimmedMsg;

  const valid =
    !disabled &&
    trimmedMsg.length > 0 && trimmedMsg.length <= MAX_MSG &&
    expiresValid &&
    TIERS[tier] && DISPLAY_POLICIES[displayPolicy] &&
    !(scopeAll === false && !venueId.trim()) &&
    !emergencyNeedsVenue;

  const go = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const body = {
        tier,
        message: trimmedMsg,
        severity,
        display_policy: displayPolicy,
        expires_at: expiresDate.toISOString(),
      };
      if (!scopeAll && venueId.trim()) body.venue_id = venueId.trim();
      await onSubmit(body, () => {
        setMessage("");
        setExpiresAt("");
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: t.card, borderRadius: 12, padding: 20, border: "1px solid " + t.border, marginBottom: 24, opacity: disabled ? 0.6 : 1 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 16 }}>📣 發布警示覆蓋</div>

      {/* tier / severity / display_policy */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>類型 (tier)</label>
          <select value={tier} onChange={e => applyTier(e.target.value)} disabled={disabled} style={fieldStyle}>
            {Object.entries(TIERS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>嚴重度 (severity)</label>
          <select value={severity} onChange={e => setSeverity(e.target.value)} disabled={disabled} style={fieldStyle}>
            {Object.entries(SEVERITIES).map(([k, v]) => <option key={k} value={k}>{v.label} ({k})</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>顯示策略 (display_policy)</label>
          <select value={displayPolicy} onChange={e => setDisplayPolicy(e.target.value)} disabled={disabled} style={fieldStyle}>
            {Object.entries(DISPLAY_POLICIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <button
          onClick={applyCwaPreset}
          disabled={disabled}
          type="button"
          style={{ padding: "8px 14px", borderRadius: 8, border: "1.5px solid " + t.danger + "66", background: t.dangerBg, color: t.danger, fontSize: 12, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer" }}
        >
          套用大雷雨災害範本
        </button>
      </div>

      {policyMismatch && (
        <div style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: t.warningBg, border: "1px solid " + t.warning + "44", fontSize: 11, color: t.warning }}>
          ⚠️ {TIERS[tier].label} 建議使用「覆蓋 30 秒 / 每 120 秒重複」顯示策略。
        </div>
      )}

      {/* scope */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>範圍 (scope)</label>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: t.text, cursor: "pointer" }}>
            <input type="radio" name="scope" checked={scopeAll} onChange={() => { setScopeAll(true); setVenueId(""); }} disabled={disabled} />
            全部場域 / 全國
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: t.text, cursor: "pointer" }}>
            <input type="radio" name="scope" checked={!scopeAll} onChange={() => setScopeAll(false)} disabled={disabled} />
            指定場域
          </label>
          {!scopeAll && (
            <input
              value={venueId}
              onChange={e => setVenueId(e.target.value)}
              placeholder="venue_id"
              disabled={disabled}
              style={{ ...fieldStyle, width: 220, fontFamily: t.mono, fontSize: 12 }}
            />
          )}
        </div>
        {emergencyNeedsVenue && (
          <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, background: t.dangerBg, border: "1px solid " + t.danger + "44", fontSize: 11, color: t.danger }}>
            ⚠️ 緊急覆蓋 (emergency) 需指定目標場域 venue_id，選擇「全國」時 B5 會回傳 422。
          </div>
        )}
      </div>

      {/* message */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>訊息內容 (message) *</label>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value.slice(0, MAX_MSG))}
          placeholder="輸入警示訊息…"
          disabled={disabled}
          maxLength={MAX_MSG}
          style={{ ...fieldStyle, height: 70, resize: "vertical" }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ fontSize: 10, color: t.dim }}>送出前會自動去除前後空白</span>
          <span style={{ fontSize: 10, color: trimmedMsg.length > MAX_MSG ? t.danger : t.dim, fontFamily: t.mono }}>{trimmedMsg.length}/{MAX_MSG}</span>
        </div>
      </div>

      {/* honest-naming preview */}
      {isDisaster && trimmedMsg && (
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>顯示預覽（伺服器實際渲染結果）</label>
          <div style={{ padding: "10px 12px", borderRadius: 8, background: t.bg, border: "1px solid " + t.danger + "44", fontSize: 13, color: t.text, lineHeight: 1.5 }}>
            {previewText}
          </div>
          {claimsCwa && (
            <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, background: t.warningBg, border: "1px solid " + t.warning + "44", fontSize: 11, color: t.warning, lineHeight: 1.5 }}>
              ⚠️ 人工轉發不得冒稱「{CWA_CLAIM}」官方自動警報；伺服器會自動移除此字樣（source=operator_manual）。預覽已反映移除後結果。
            </div>
          )}
        </div>
      )}

      {/* expires_at */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>到期時間 (expires_at) *</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {TTL_PRESETS.map(p => (
            <button key={p.label} onClick={() => setTtl(p.ms)} disabled={disabled} type="button"
              style={{ padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, border: "1.5px solid " + t.border, background: t.bg, color: t.muted, cursor: disabled ? "not-allowed" : "pointer" }}>
              {p.label}
            </button>
          ))}
        </div>
        <input
          type="datetime-local"
          value={expiresAt}
          onChange={e => setExpiresAt(e.target.value)}
          disabled={disabled}
          style={{ ...fieldStyle, fontFamily: t.mono }}
        />
        {expiresAt && !expiresValid && (
          <div style={{ marginTop: 6, fontSize: 11, color: t.danger }}>到期時間必須是未來的時間。</div>
        )}
      </div>

      {/* exact outgoing request preview */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>送出內容預覽</label>
        <div style={{ padding: "12px 14px", borderRadius: 8, background: t.bg, border: "1px solid " + t.border, fontFamily: t.mono, fontSize: 11, color: t.text, lineHeight: 1.8, overflowWrap: "anywhere" }}>
          <div><span style={{ color: t.muted }}>tier=</span>{tier}</div>
          <div><span style={{ color: t.muted }}>severity=</span>{severity}</div>
          <div><span style={{ color: t.muted }}>display_policy=</span>{displayPolicy}</div>
          <div><span style={{ color: t.muted }}>scope=</span>{scopeAll ? "全部場域" : (venueId.trim() || "未指定場域")}</div>
          <div><span style={{ color: t.muted }}>expires_at=</span>{expiresValid ? expiresDate.toISOString() : "未設定或無效"}</div>
          <div><span style={{ color: t.muted }}>message=</span>{trimmedMsg || "未輸入"}</div>
        </div>
        {isDisaster && (
          <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, background: t.dangerBg, border: "1px solid " + t.danger + "44", fontSize: 11, color: t.danger }}>
            伺服器將以 brioCAST 災害警示 呈現人工轉發訊息
          </div>
        )}
      </div>

      <button
        onClick={go}
        disabled={!valid || busy}
        style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: valid && !busy ? t.accent : t.dim, color: "#fff", fontSize: 14, fontWeight: 700, cursor: valid && !busy ? "pointer" : "not-allowed", opacity: valid && !busy ? 1 : 0.6 }}
      >
        {busy ? "發布中…" : "🚨 發布警示覆蓋"}
      </button>
      {disabled && (
        <div style={{ marginTop: 10, fontSize: 11, color: t.muted, textAlign: "center" }}>請先於上方貼上 admin token 才能發布或取消警示。</div>
      )}
    </div>
  );
}

// ── Active overrides list ──
function ActiveList({ disabled, items, loading, onCancel }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 12 }}>📡 進行中的警示覆蓋</div>
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: t.dim }}>載入中…</div>
      ) : (!Array.isArray(items) || items.length === 0) ? (
        <div style={{ padding: 40, textAlign: "center", background: t.card, borderRadius: 10, border: "1px solid " + t.border }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>目前沒有進行中的警示</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map(o => {
            const tierInfo = TIERS[o.tier] || { label: o.tier, color: t.muted };
            const sev = SEVERITIES[o.severity] || { label: o.severity, color: t.muted, bg: "rgba(90,94,114,0.15)" };
            const dp = DISPLAY_POLICIES[o.display_policy] || { label: o.display_policy };
            const active = o.status === "active";
            return (
              <div key={o.id} style={{ background: t.card, borderRadius: 10, padding: "12px 16px", border: "1px solid " + t.border }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                  <Badge label={tierInfo.label} color={tierInfo.color} />
                  <Badge label={sev.label} color={sev.color} bg={sev.bg} />
                  <span style={{ fontSize: 11, color: t.muted }}>{dp.label}</span>
                  {o.status && o.status !== "active" && <Badge label={o.status} color={t.muted} bg="rgba(90,94,114,0.15)" />}
                  <span style={{ marginLeft: "auto", fontSize: 11, color: o.venue_id ? t.text : t.accent, fontFamily: t.mono }}>
                    {o.venue_id ? "🎯 " + o.venue_id : "🌐 全部場域"}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: t.text, lineHeight: 1.5, marginBottom: 8 }}>{o.message || o.text}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, color: t.dim, fontFamily: t.mono }}>開始 {fmt(o.starts_at)}</span>
                  <span style={{ fontSize: 10, color: t.dim, fontFamily: t.mono }}>到期 {fmt(o.expires_at)}</span>
                  {o.source && <span style={{ fontSize: 10, color: t.dim, fontFamily: t.mono }}>來源 {o.source}</span>}
                  {active && (
                    <button
                      onClick={() => onCancel(o)}
                      disabled={disabled}
                      style={{ marginLeft: "auto", padding: "6px 14px", borderRadius: 8, border: "1.5px solid " + t.danger + "66", background: "none", color: t.danger, fontSize: 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1 }}
                    >取消</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================
export default function AdminAlertConsolePage() {
  const [token, setToken] = useState(getAdminToken());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const hasToken = !!token;

  const setAdminToken = (next) => {
    sessionStorage.setItem(ADMIN_TOKEN_KEY, next);
    setToken(next);
  };
  const clearAdminToken = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken("");
  };

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const handleError = (e, fallback) => {
    console.error(`[AdminAlertConsole] ${fallback} — status=${e.status ?? "?"}: ${e.message}`);
    if (e.status === 401 || e.status === 403) {
      showToast("admin token 無效或過期，請重新貼上", "error");
    } else {
      showToast(fallback + (e.message ? `：${e.message}` : ""), "error");
    }
  };

  const loadList = useCallback(async () => {
    if (!getAdminToken()) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await adminApi("?active=true");
      const list = Array.isArray(r) ? r : (r?.data?.overrides || r?.overrides || (Array.isArray(r?.data) ? r.data : []));
      setItems(list);
    } catch (e) {
      console.error(`[AdminAlertConsole] 載入警示列表失敗 — status=${e.status ?? "?"}: ${e.message}`);
      if (e.status === 401 || e.status === 403) showToast("admin token 無效或過期，請重新貼上", "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList, token]);

  useEffect(() => {
    if (toast) { const id = setTimeout(() => setToast(null), 3500); return () => clearTimeout(id); }
  }, [toast]);

  const create = async (body, onOk) => {
    if (!getAdminToken()) { showToast("請先貼上 admin token", "error"); return; }
    try {
      const response = await adminApi("", { method: "POST", body });
      const candidates = [
        response?.data?.override,
        response?.override,
        response?.data,
        response,
      ];
      const created = candidates.find(candidate =>
        candidate &&
        typeof candidate === "object" &&
        !Array.isArray(candidate) &&
        (
          "tier" in candidate ||
          "display_policy" in candidate
        )
      );
      const responseMismatch = created && (
        created.tier !== body.tier ||
        created.display_policy !== body.display_policy ||
        (created.status != null && created.status !== "active")
      );

      if (responseMismatch) {
        showToast("發布結果與送出內容不一致，請重新整理確認", "error");
        await loadList();
        return;
      }

      const successMessages = {
        cwa: "CWA 災害人工轉發已發布",
        platform: "平台公告已發布",
        emergency: "緊急覆蓋已發布",
      };
      showToast(successMessages[body.tier] || "警示覆蓋已發布", "success");
      onOk?.();
      await loadList();
    } catch (e) {
      handleError(e, "發布失敗");
    }
  };

  const cancel = async (o) => {
    if (!getAdminToken()) { showToast("請先貼上 admin token", "error"); return; }
    try {
      await adminApi(`/${o.id}/cancel`, { method: "PATCH" });
      showToast("已取消警示覆蓋", "success");
      loadList();
    } catch (e) {
      handleError(e, "取消失敗");
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {toast && (
        <div onClick={() => setToast(null)} style={{ position: "fixed", top: 16, right: 16, zIndex: 999, padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", background: toast.type === "error" ? t.dangerBg : t.successBg, color: toast.type === "error" ? t.danger : t.success, border: "1px solid " + (toast.type === "error" ? t.danger : t.success) + "33", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: t.text, margin: 0 }}>🚨 警示控制台</h2>
          <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, color: t.danger, background: t.dangerBg, letterSpacing: 0.5 }}>ADMIN</span>
        </div>
        <div style={{ fontSize: 12, color: t.muted, marginTop: 4, lineHeight: 1.5 }}>
          供 brioCAST 操作人員發布平台公告、災害人工轉發與緊急覆蓋訊息。店家無法使用此功能。
        </div>
        <div style={{ fontSize: 11, color: t.dim, marginTop: 6, lineHeight: 1.5 }}>
          人工轉發災害訊息會顯示為「brioCAST 災害警示」，不得冒稱中央氣象署官方自動警報。
        </div>
      </div>

      <TokenPanel token={token} onChange={setAdminToken} onClear={clearAdminToken} />

      <CreateForm disabled={!hasToken} onSubmit={create} />

      <ActiveList disabled={!hasToken} items={items} loading={loading} onCancel={cancel} />
    </div>
  );
}
